import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DebtIssuerDistributionChartProps } from "./types";
import { groupByIssuer } from "./utils";
import { formatDebtCurrency } from "./utils";

const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8",
  "#82CA9D", "#FFC658", "#FF6B9D", "#C084FC", "#34D399",
];

/**
 * Pie chart showing debt securities distribution by issuer.
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 5.2
 */
export function DebtIssuerDistributionChart({
  debtSecurities,
  currency,
  isLoading,
}: DebtIssuerDistributionChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribution by Issuer</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const groups = groupByIssuer(debtSecurities);

  const chartData = groups.map((g, index) => ({
    name: g.name,
    value: g.total,
    percentage: g.percentage.toFixed(2),
    color: COLORS[index % COLORS.length],
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribution by Issuer</CardTitle>
      </CardHeader>
      <CardContent>
        <div aria-label="Debt securities distribution by issuer">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percentage }) => `${percentage}%`}
                outerRadius={80}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatDebtCurrency(value, currency),
                  name,
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
